// REISOR_STAGE22A_DRIVER_TRIAL_ACCESS_SUBSCRIPTION_V1
// Legacy component name kept for compatibility; UI is plain "share app" + access/subscription info.
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Share } from "@capacitor/share";
import { buildDriverReferralSharePayload } from "../utils/driverReferralPromo";
import { getDriverReferralPromoCopy } from "../utils/driverReferralPromoCopy";

function accessText(copy, access) {
  const a = access && typeof access === "object" ? access : {};
  const reason = String(a.reason || "").trim();

  if (a.hasActiveSubscription) {
    return a?.subscription?.text || (a?.subscription?.currency === "RUB" ? copy.subscriptionRu : copy.subscriptionUz);
  }
  if (a.trialFirstOrderAvailable || reason === "trial_first_order_available") return copy.trialAvailable;
  if (a.trialFirstOrderUsedAt || reason === "trial_first_order_used") return copy.trialUsed;
  if (a.trialExpired || reason === "trial_first_order_expired") return copy.trialExpired;

  return a?.message || copy.trialAvailable;
}

export default function DriverReferralPromoCard({ token, driver }) {
  const { i18n } = useTranslation();
  const lang = String(i18n.resolvedLanguage || i18n.language || "ru").split("-")[0];
  const copy = useMemo(() => getDriverReferralPromoCopy(lang), [lang]);
  const payload = useMemo(
    () => buildDriverReferralSharePayload({ token, driver, lang }),
    [token, driver, lang]
  );
  const [copied, setCopied] = useState(false);

  const driverAccess = driver?.driverAccess || driver?.access || null;
  const text = accessText(copy, driverAccess);

  const share = async () => {
    const title = copy.shareTitle || "REISOR";
    try {
      await Share.share({
        title,
        text: String(payload.text || "")
          .replace(payload.link ? `\n${payload.link}` : "", "")
          .trim(),
        url: payload.link,
        dialogTitle: copy.shareButton,
      });
    } catch {
      try {
        await navigator.clipboard.writeText(payload.text || payload.link || "");
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1800);
      } catch {}
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(payload.link || "");
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {}
  };

  return (
    <div className="rounded-3xl border border-slate-700 bg-slate-800/40 p-4 text-slate-100">
      <div className="text-lg font-black">{copy.cardTitle}</div>
      <div className="mt-2 text-sm leading-6 text-slate-300">{copy.cardText}</div>

      <div className="mt-4 rounded-2xl border border-emerald-500/25 bg-emerald-500/10 p-3 text-sm leading-6 text-emerald-50">
        {text}
      </div>

      <div className="mt-4">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">
          {copy.inviteLink}
        </div>
        <div className="mt-1 break-all rounded-2xl border border-slate-700 bg-slate-950/50 px-3 py-2 text-sm text-slate-200">
          {payload.link}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={share}
          className="rounded-2xl border border-blue-500/30 bg-blue-500/20 px-4 py-2 text-sm font-bold text-blue-50"
        >
          {copy.shareButton}
        </button>
        <button
          type="button"
          onClick={copyLink}
          className="rounded-2xl border border-slate-600 bg-slate-900/50 px-4 py-2 text-sm font-bold text-slate-100"
        >
          {copied ? copy.copiedButton : copy.copyButton}
        </button>
      </div>
    </div>
  );
}
